import { useCallback, useMemo, useState } from 'react'
import {
  useListCalendars,
  type CalendarOption,
} from 'services/calendar-integration'

interface UseCalendarsResult {
  calendars: CalendarOption[]
  selectedId: number | null
  setSelectedId: (id: number | null) => void
  loading: boolean
}

/**
 * Loads calendars for a given integration via React Query and tracks
 * which calendar the user wants to commit. The selected id falls back
 * to the server-selected calendar, then the primary calendar, then
 * `null`. The override is scoped to the current `integrationId` so a
 * different integration resets the user's choice without an effect.
 */
export function useCalendars(
  integrationId: number | null,
  enabled: boolean
): UseCalendarsResult {
  const queryEnabled = enabled && !!integrationId
  const { data, isFetching } = useListCalendars(
    { integration_id: integrationId ?? 0 },
    {
      query: {
        enabled: queryEnabled,
        staleTime: 0,
      },
    }
  )

  const calendars: CalendarOption[] = queryEnabled && data ? data : []

  const defaultSelectedId = useMemo(() => {
    if (!calendars.length) return null
    const pre =
      calendars.find((c) => c.is_selected) ??
      calendars.find((c) => c.is_primary)
    return pre?.id ?? null
  }, [calendars])

  // Override scoped to a specific integrationId so a different integration
  // resets the user's choice without an effect-based reset.
  const [override, setOverride] = useState<{
    integrationId: number | null
    id: number | null
  } | null>(null)

  const selectedId =
    override && override.integrationId === integrationId
      ? override.id
      : defaultSelectedId

  const setSelectedId = useCallback(
    (id: number | null) => setOverride({ integrationId, id }),
    [integrationId]
  )

  return { calendars, selectedId, setSelectedId, loading: isFetching }
}
